import { supabase } from './supabase.js';
import { coerceUrl } from './url.js';
import { SUPPORTED_LANGUAGES } from '../i18n.js';

const SELECT_COLUMNS = [
  'id',
  'title',
  'type',
  'description',
  'author',
  'institution',
  'url',
  'language',
  'level',
  'status',
  'submitted_by',
  'created_at',
  'updated_at',
].join(', ');

export const TEACHING_TYPES = ['syllabus', 'course_outline', 'lecture', 'reading_list', 'exercise', 'other'];
export const TEACHING_LEVELS = ['undergraduate', 'graduate', 'professional', 'general'];

export const TEACHING_LANGUAGES = [
  ...SUPPORTED_LANGUAGES,
  { code: 'gl', name: 'Galego' },
  { code: 'hu', name: 'Magyar' },
  { code: 'pl', name: 'Polski' },
];

export const EMPTY_TEACHING = {
  title: '',
  type: 'syllabus',
  description: '',
  author: '',
  institution: '',
  url: '',
  language: '',
  level: '',
};

export const EMPTY_TEACHING_FILTERS = {
  type: '',
  level: '',
  language: '',
};

export async function fetchPublishedTeaching() {
  const { data, error } = await supabase
    .from('teaching_materials')
    .select(SELECT_COLUMNS)
    .eq('status', 'published')
    .order('created_at', { ascending: false });
  if (error) throw error;
  return data ?? [];
}

export async function fetchTeaching(id) {
  const { data, error } = await supabase
    .from('teaching_materials')
    .select(SELECT_COLUMNS)
    .eq('id', id)
    .maybeSingle();
  if (error) throw error;
  return data;
}

function normalize(form) {
  const blank = (v) => (v == null || v === '' ? null : v);
  return {
    title: form.title?.trim(),
    type: form.type,
    description: blank(form.description),
    author: blank(form.author?.trim?.()),
    institution: blank(form.institution?.trim?.()),
    url: coerceUrl(form.url),
    language: blank(form.language),
    level: blank(form.level),
  };
}

export function validateTeaching(form) {
  if (!form.title?.trim()) return 'teaching.form.errors.titleRequired';
  if (!TEACHING_TYPES.includes(form.type)) return 'teaching.form.errors.typeRequired';
  return null;
}

// announce_in_whats_new is only written on create; see migration 019.
export async function createTeaching(form, { authorId, asAdmin }) {
  const row = {
    ...normalize(form),
    announce_in_whats_new: Boolean(form.announce_in_whats_new),
    status: asAdmin ? 'published' : 'pending',
    submitted_by: authorId,
  };
  const { data, error } = await supabase
    .from('teaching_materials')
    .insert(row)
    .select(SELECT_COLUMNS)
    .single();
  if (error) throw error;
  return data;
}

export async function updateTeaching(id, form) {
  const { data, error } = await supabase
    .from('teaching_materials')
    .update(normalize(form))
    .eq('id', id)
    .select(SELECT_COLUMNS)
    .single();
  if (error) throw error;
  return data;
}

export async function deleteTeaching(id) {
  const { error } = await supabase.from('teaching_materials').delete().eq('id', id);
  if (error) throw error;
}

export function filterTeaching(items, { type, level, language, query }) {
  const q = (query ?? '').trim().toLowerCase();
  return items.filter((m) => {
    if (type && m.type !== type) return false;
    if (level && m.level !== level) return false;
    if (language && m.language !== language) return false;
    if (q) {
      const haystack = [m.title, m.author, m.institution, m.description]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      if (!haystack.includes(q)) return false;
    }
    return true;
  });
}

export function deriveTeachingFacets(items) {
  const types = new Map();
  const levels = new Map();
  const languages = new Map();
  for (const m of items) {
    if (m.type) types.set(m.type, (types.get(m.type) ?? 0) + 1);
    if (m.level) levels.set(m.level, (levels.get(m.level) ?? 0) + 1);
    if (m.language) languages.set(m.language, (languages.get(m.language) ?? 0) + 1);
  }
  return {
    types: TEACHING_TYPES.map((t) => ({ type: t, count: types.get(t) ?? 0 })),
    levels: TEACHING_LEVELS.map((l) => ({ level: l, count: levels.get(l) ?? 0 })),
    languages: [...languages.entries()]
      .map(([code, count]) => {
        const match = TEACHING_LANGUAGES.find((l) => l.code === code);
        return { code, name: match?.name ?? code, count };
      })
      .sort((a, b) => a.name.localeCompare(b.name)),
  };
}
